import { useCallback, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";
import { hapticSelection } from "../../../../lib/haptics";
import { focusRingSx, touchTargetSx } from "../../overviewPanel";
import { TABULAR, type TonalTokens } from "../../../polish/tokens";

/**
 * One page of a card's list. Later pages keep the first page's height, so a
 * short last page never moves the pager; another list (another report)
 * starts again at the first page.
 */
export const usePaged = <T,>(items: readonly T[], pageSize: number) => {
  const [state, setState] = useState({ items, page: 0 });
  const [fullHeight, setFullHeight] = useState<number>();
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const page = state.items === items ? Math.min(state.page, pages - 1) : 0;
  const setPage = useCallback(
    (next: number) => setState({ items, page: next }),
    [items],
  );
  const listRef = useCallback(
    (node: HTMLElement | null) => {
      if (node !== null && page === 0) setFullHeight(node.offsetHeight);
    },
    [page],
  );
  return {
    shown: items.slice(page * pageSize, (page + 1) * pageSize),
    page,
    pages,
    setPage,
    listRef,
    listMinHeight: page > 0 ? fullHeight : undefined,
  };
};

interface PagerProps {
  page: number;
  pages: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
  tokens: TonalTokens;
  /** What is paged, for the control's accessible name. */
  label: string;
}

/** "1–8 of 18" with the previous and next page; nothing for a single page. */
export const Pager = ({
  page,
  pages,
  pageSize,
  total,
  onPage,
  tokens,
  label,
}: PagerProps) => {
  if (pages < 2) return null;
  const first = page * pageSize + 1;
  const last = Math.min(total, (page + 1) * pageSize);
  const go = (next: number) => {
    hapticSelection();
    onPage(next);
  };
  const buttonSx = {
    color: tokens.muted,
    ...touchTargetSx,
    ...focusRingSx,
    "@media (hover: hover)": {
      "&:hover": { color: tokens.text, backgroundColor: tokens.raised },
    },
    "&.Mui-disabled": { color: tokens.faint, opacity: 0.4 },
  } as const;

  return (
    <Box
      component="nav"
      aria-label={`${label} pages`}
      data-testid="pager"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 0.5,
        pt: 1,
        borderTop: `1px solid ${tokens.hairline}`,
      }}
    >
      <Typography
        data-testid="pager-range"
        sx={{
          ...TABULAR,
          fontSize: "0.8125rem",
          color: tokens.faint,
          mr: 1,
        }}
      >
        {first}–{last} of {total}
      </Typography>
      <IconButton
        size="small"
        aria-label="Previous page"
        disabled={page === 0}
        onClick={() => go(page - 1)}
        sx={buttonSx}
      >
        <ChevronLeftRounded fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Next page"
        disabled={page === pages - 1}
        onClick={() => go(page + 1)}
        sx={buttonSx}
      >
        <ChevronRightRounded fontSize="small" />
      </IconButton>
    </Box>
  );
};
